import clsx from "clsx";
import type { HTMLAttributes, ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleMinus,
  faCircleNotch,
  faCircleXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { ToolCallStatus } from "@/components/ui/agentic-shared/types";
import { AGENTIC_STATUS_ICON } from "@/components/ui/agentic-shared/statusTone";

export type AgenticToolCallRowProps = {
  toolName: string;
  label?: string;
  status?: ToolCallStatus;
  summary?: string;
  detail?: ReactNode;
  detailClassName?: string;
  className?: string;
  /** When true, omit the Running/Used/Failed status word (label is already a full phrase). */
  hidePrefix?: boolean;
  /** When set, the status line is clickable (expand/collapse tool detail). */
  onHeaderActivate?: () => void;
  headerAriaExpanded?: boolean;
};

function statusMeta(status: ToolCallStatus) {
  switch (status) {
    case "running":
      return {
        icon: faCircleNotch,
        tone: `${AGENTIC_STATUS_ICON.running} animate-spin`,
        prefix: "Running",
      };
    case "error":
      return {
        icon: faCircleXmark,
        tone: AGENTIC_STATUS_ICON.error,
        prefix: "Failed",
      };
    case "pending":
      return {
        icon: faCircleExclamation,
        tone: AGENTIC_STATUS_ICON.pending,
        prefix: "Pending",
      };
    case "cancelled":
      return {
        icon: faCircleMinus,
        tone: AGENTIC_STATUS_ICON.cancelled,
        prefix: "Stopped",
      };
    default:
      return {
        icon: faCircleCheck,
        tone: AGENTIC_STATUS_ICON.success,
        prefix: "Used",
      };
  }
}

export function AgenticToolCallRow({
  toolName,
  label,
  status = "success",
  summary,
  detail,
  detailClassName,
  className,
  hidePrefix = false,
  onHeaderActivate,
  headerAriaExpanded,
  ...rest
}: AgenticToolCallRowProps & HTMLAttributes<HTMLDivElement>) {
  const meta = statusMeta(status);
  const displayLabel = label ?? toolName;
  const headerInteractive = typeof onHeaderActivate === "function";

  return (
    <div
      className={clsx("space-y-1 pl-1", className)}
      data-testid="agentic-tool-call-row"
      aria-busy={status === "running"}
      {...rest}
    >
      <div
        className={clsx(
          "flex items-center gap-2 py-0.5 text-xs text-slate-600 dark:text-slate-400",
          headerInteractive &&
            "cursor-pointer rounded outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40",
        )}
        role={headerInteractive ? "button" : undefined}
        tabIndex={headerInteractive ? 0 : undefined}
        aria-expanded={headerInteractive ? headerAriaExpanded : undefined}
        onClick={headerInteractive ? onHeaderActivate : undefined}
        onKeyDown={
          headerInteractive
            ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") {
                  return;
                }
                event.preventDefault();
                onHeaderActivate?.();
              }
            : undefined
        }
      >
        <FontAwesomeIcon icon={meta.icon} className={clsx("shrink-0 text-[10px]", meta.tone)} />
        <span className="min-w-0">
          {!hidePrefix ? `${meta.prefix} ` : null}
          <span className="text-slate-800 dark:text-slate-300">{displayLabel}</span>
          {summary ? <span className="text-slate-500 dark:text-slate-500"> — {summary}</span> : null}
        </span>
      </div>
      {detail ? <div className={clsx("ml-4", detailClassName)}>{detail}</div> : null}
    </div>
  );
}
